import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { FilterNode } from '@ofmos/shared/filter-ast';
import { encodeGroupKey, type RecordGroupTotals } from '@ofmos/shared/record-group';
import { type OrderBy, RECORD_QUERY_DEFAULT_LIMIT } from '@ofmos/shared/record-query';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type {
  FieldMetadata,
  ObjectMetadata,
  RecordGroupEntry,
  RecordRow,
} from '../../../data/client';
import { pagedIds, useInfiniteRecordList, useUpdateRecord } from '../../../data/hooks';
import { queryKeys } from '../../../data/queryKeys';
import { TagDot } from '../../../fields/display/TagDot';
import { Button, cx, Icon, Skeleton, Spinner, useToast } from '../../../ui';
import { MOTION } from '../../../ui/motion';
import type { GroupDisplay } from './groupDisplay';
import {
  applyGroupDrop,
  encodedKeyOfDropId,
  groupDropId,
  recordIdOfRowDragId,
  rowDragId,
} from './groupDrop';
import {
  acceptsGroupDrops,
  aggregateLines,
  groupCountLabel,
  groupDropValue,
  groupLabel,
  groupRowsFilter,
  groupTone,
} from './groupModel';
import { TableColgroup, TableHead, TableSurface } from './RecordTable';
import { PlaceholderRow, RecordTableRow } from './RecordTableRow';
import { recordTitle, type TableColumn } from './tableModel';
import { type GroupedTable, headerAggregate } from './useGroupedTable';
import { usePresentRows } from './usePresentRows';

/**
 * THE GROUPED TABLE (`docs/decisions/group-by-aggregation.md` D22): a group layer over the
 * same rows, and the product's first `<tfoot>`.
 *
 * ONE TABLE, NOT ONE PER GROUP. A group is a `<tbody>`, which is what HTML already has for
 * "a band of rows with a heading": the `<colgroup>` is shared, so a number in a section
 * footer sits under the column it counts, and the sticky column head keeps working while
 * the reader scrolls through the sections.
 *
 * THE SAME CARD AND THE SAME SCROLLPORT AS THE FLAT TABLE (`TableSurface`, polish wave 2).
 * It carried a copy of the flat table's card wrapper and therefore a copy of its defect -
 * the audit could not photograph a grouped table and said so, and P1-1 applied to this file
 * "by code, not by picture". One surface component now, so the next scroll decision cannot
 * land on one of the two tables.
 *
 * THE FOOTER'S HEIGHT DEPENDS ON THE DATA (D18, D22). A CURRENCY cell is one line per
 * discovered code, so a two-currency instance makes a two-line footer and an eight-currency
 * one makes eight (`CURRENCY_CODES_MAX` is what stops it being forty). Nothing here fixes a
 * height: a fixed one would clip the second currency line, and the `<colgroup>` fixes the
 * WIDTHS so a multi-line cell wraps inside its own column instead of pushing its
 * neighbours.
 *
 * DOM SIZE IS BOUNDED BY WHAT SOMEBODY OPENED. There is no virtualization anywhere in this
 * package, and at D20's caps a fully expanded grouped table would be 10,000 real rows, so
 * groups past the first ten render closed (`groupDisplay.ts`).
 *
 * NOTHING HERE INVENTS A NUMBER. A group with no rows is either synthesised from a closed
 * domain, one layer up, or absent; a value operation over nothing renders an em dash rather
 * than a zero; and the truncation row says the list was cut instead of simply stopping,
 * which is the one thing D20 refuses to ship.
 */
export interface GroupedRecordTableProps {
  readonly object: ObjectMetadata;
  readonly columns: readonly TableColumn[];
  /** Every field of the object, for the row menu. See `RecordTableRow`. */
  readonly fields: readonly FieldMetadata[];
  readonly boardGroupFieldId: string | null;
  readonly grouped: GroupedTable;
  readonly display: GroupDisplay;
  /** The VIEW's filter; a group's own condition is ANDed onto it for "show all" (D19). */
  readonly viewFilter: FilterNode | undefined;
  readonly orderBy: readonly OrderBy[] | undefined;
  /** Every field id, exactly as the flat list selects them (INV-13). */
  readonly select: readonly string[];
  readonly archived: boolean;
}

export function GroupedRecordTable({
  object,
  columns,
  fields,
  boardGroupFieldId,
  grouped,
  display,
  viewFilter,
  orderBy,
  select,
  archived,
}: GroupedRecordTableProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const updateRecord = useUpdateRecord(object.id);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const draggable = acceptsGroupDrops(grouped.resolved) && !archived;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // A few pixels of travel rather than a delay: the handle is its own control, so a
      // pointer that lands on it is not about to edit a cell.
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
    }),
  );

  const groups = grouped.groups;

  const handleDragEnd = (event: DragEndEvent) => {
    const recordId = recordIdOfRowDragId(event.active.id);
    setDraggedId(null);
    if (recordId === null || groups === undefined) return;

    const targetKey = encodedKeyOfDropId(event.over?.id);
    if (targetKey === null) return;
    /**
     * WHICH GROUP THE ROW CAME FROM is carried by the handle, not looked up in the seeded
     * rows: a group that has been EXPANDED renders the ordinary infinite list instead of
     * its preview (D19), so `group.records` no longer contains the row - and without this
     * a drop back into its own group would fall through the guard below and write the
     * value the field already holds.
     */
    const sourceKey = sourceKeyOf(event.active.data.current);
    if (sourceKey === targetKey) return;
    const target = groups.find((group) => encodeGroupKey(group.key) === targetKey);
    if (target === undefined) return;
    const from = groups.find((group) => encodeGroupKey(group.key) === sourceKey);

    // A bucketed dimension never gets here - it registers no drop targets - and this is
    // the second half of the same refusal rather than a duplicate of it: the value is what
    // a bucket cannot produce, because "March" would have to invent a day.
    const drop = groupDropValue(target.key);
    if (drop === null) return;

    const row = queryClient.getQueryData<RecordRow>(queryKeys.record(object.id, recordId));
    applyGroupDrop({
      input: {
        recordId,
        groupField: grouped.resolved.field,
        value: drop.value,
        recordTitle:
          row === undefined ? 'The record' : recordTitle(row, object.labelIdentifierFieldId),
        fromColumnLabel:
          from === undefined ? 'its group' : groupLabel(from.key, grouped.resolved, from.labels),
      },
      write: updateRecord.mutate,
      toast,
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    setDraggedId(recordIdOfRowDragId(event.active.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDraggedId(null)}
    >
      <TableSurface label={object.labelPlural} columns={columns} busy={groups === undefined}>
        {/*
        THE ARROW OVER THE WHOLE TABLE (wave 4, and L-B1's rule one row further up). The row
        and its cells declare `cursor: default` in the stylesheet, but the `<table>` and its
        COLUMN HEADS did not: they stood at `cursor: auto`, which over a heading's text is the
        I-beam - the same "als wuerde ich einen text auswaehlen" the owner named on the cells.
        `cursor` inherits, so one declaration on the table covers the heads, the table's own
        box and anything a later column adds; a control inside still takes its own.
        hig: https://developer.apple.com/design/human-interface-guidelines/pointing-devices,
        where the Arrow is "Standard pointer for selecting and interacting with content and
        interface elements" and the horizontal I beam means "Selection and insertion of text
        is possible in a horizontal layout, such as a TextEdit or Pages document".
      */}
        <table
          className="min-w-full table-fixed cursor-default border-collapse text-sm"
          style={{ width: `${columns.reduce((sum, column) => sum + column.width, 0)}rem` }}
        >
          <TableColgroup columns={columns} />
          <TableHead columns={columns} accessibleOnly />

          {groups === undefined ? (
            <tbody>
              {PLACEHOLDER_GROUPS.map((key) => (
                <PlaceholderRow key={key} columns={columns} />
              ))}
            </tbody>
          ) : (
            groups.map((group, index) => (
              <GroupSection
                key={encodeGroupKey(group.key)}
                object={object}
                columns={columns}
                fields={fields}
                boardGroupFieldId={boardGroupFieldId}
                grouped={grouped}
                group={group}
                index={index}
                display={display}
                viewFilter={viewFilter}
                orderBy={orderBy}
                select={select}
                archived={archived}
                draggable={draggable}
              />
            ))
          )}

          {grouped.hasMoreGroups && (
            <tbody>
              <tr>
                <td colSpan={columns.length} className="data-cell border-t border-line bg-surface">
                  {/*
                    D20: ABOVE THE CAP THE LIST TRUNCATES VISIBLY. `csv-import-export.md`
                    D35 refuses an over-cap export outright, and the operative word there is
                    SILENTLY: a CSV leaves the product and looks complete forever, while a
                    shortened group list is on a screen that can carry a sentence, next to
                    the control that fixes it. What is NOT acceptable is a list that simply
                    stops, which is what Twenty ships.

                    It says the first N and never "N of 312": counting the distinct values
                    would be a second full scan of the filter.
                  */}
                  <p className="text-sm text-ink-muted">
                    {`These are the first ${String(grouped.groupLimit)} groups, and there are more. Narrow the filter, or group by a field with fewer values, to see the rest.`}
                  </p>
                </td>
              </tr>
            </tbody>
          )}

          {grouped.plan.footerIndex.size > 0 && (
            <GroupTotalsFoot
              columns={columns}
              grouped={grouped}
              totals={grouped.total}
              label="All groups"
              sticky
            />
          )}
        </table>
      </TableSurface>

      {/* No drop animation: the row is already written, and a row flying back to where it
          started reads as a failed move. */}
      <DragOverlay dropAnimation={null}>
        {draggedId !== null && (
          <DraggedRowChip
            objectId={object.id}
            recordId={draggedId}
            titleFieldId={object.labelIdentifierFieldId}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}

const PLACEHOLDER_GROUPS = ['1', '2', '3', '4', '5', '6'] as const;

interface GroupSectionProps {
  readonly object: ObjectMetadata;
  readonly columns: readonly TableColumn[];
  readonly fields: readonly FieldMetadata[];
  readonly boardGroupFieldId: string | null;
  readonly grouped: GroupedTable;
  readonly group: RecordGroupEntry;
  readonly index: number;
  readonly display: GroupDisplay;
  readonly viewFilter: FilterNode | undefined;
  readonly orderBy: readonly OrderBy[] | undefined;
  readonly select: readonly string[];
  readonly archived: boolean;
  readonly draggable: boolean;
}

/**
 * ONE GROUP: a heading row, its rows, and its own totals.
 *
 * The heading is a real `<button>` with `aria-expanded` filling the row, which is the
 * disclosure pattern this product already uses (`CollapsibleSection`) - a chevron alone is
 * a 16px target on a phone, and the numbers beside the name belong to the same control
 * because they are what the reader is deciding about when they open it.
 */
function GroupSection({
  object,
  columns,
  fields,
  boardGroupFieldId,
  grouped,
  group,
  index,
  display,
  viewFilter,
  orderBy,
  select,
  archived,
  draggable,
}: GroupSectionProps) {
  const encoded = encodeGroupKey(group.key);
  const { setNodeRef, isOver } = useDroppable({ id: groupDropId(encoded), disabled: !draggable });
  const open = display.isOpen(encoded, index);
  const expanded = display.isExpanded(encoded);

  const label = groupLabel(group.key, grouped.resolved, group.labels);
  const tone = groupTone(group.key, grouped.resolved);
  const header = headerAggregate(grouped, group);
  const preview = usePresentRows(
    group.records.map((row) => row.id),
    open && !expanded,
  );

  return (
    <tbody ref={setNodeRef} className="border-t border-line">
      <tr>
        <th
          scope="colgroup"
          colSpan={columns.length}
          className={cx(
            'data-head text-left',
            // The single accent, used for exactly one thing: the group a dragged row would
            // land in. On the HEADING rather than on the `<tbody>`, so the answer is where
            // the reader is looking and does not depend on a table section painting a
            // background.
            isOver ? 'bg-accent-subtle' : 'bg-surface-sunken',
          )}
        >
          <button
            type="button"
            aria-expanded={open}
            onClick={() => display.toggleOpen(encoded, index)}
            className={cx(
              'pressable flex min-h-(--touch-target) w-full items-center gap-3 rounded-control px-1',
              'text-left hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-accent',
            )}
          >
            <Icon
              name="chevron-right"
              size="0.9rem"
              className={cx('shrink-0 text-ink-subtle', MOTION.turn, open && 'rotate-90')}
            />
            {tone === null ? (
              <span className="truncate text-sm font-semibold text-ink">{label}</span>
            ) : (
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <TagDot tone={tone} />
                <span className="truncate text-ink">{label}</span>
              </span>
            )}
            <span className="shrink-0 text-2xs tracking-wide text-ink-muted">
              {groupCountLabel(group.count)}
            </span>
            {/*
              BESIDE THE NAME, NOT AT THE FAR EDGE. The number belongs to the GROUP, and a
              right-aligned figure in a full-width heading lands under whichever column
              happens to be last - which reads as that column's value and is the one thing a
              table full of numbers must not do. It carries its own name for the same
              reason: "60.14" alone says nothing about what was averaged.
            */}
            {header !== null && (
              <span className="flex min-w-0 shrink items-baseline gap-1.5 text-xs text-ink-muted">
                <span className="truncate">{header.label}</span>
                <span className="flex flex-col tabular-nums text-ink">
                  {header.lines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </span>
              </span>
            )}
          </button>
        </th>
      </tr>

      {open &&
        (expanded ? (
          <ExpandedGroupRows
            object={object}
            columns={columns}
            fields={fields}
            boardGroupFieldId={boardGroupFieldId}
            grouped={grouped}
            group={group}
            viewFilter={viewFilter}
            orderBy={orderBy}
            select={select}
            archived={archived}
            draggable={draggable}
          />
        ) : (
          <>
            {preview.ids.map((recordId) => (
              <RecordTableRow
                key={recordId}
                object={object}
                columns={columns}
                fields={fields}
                boardGroupFieldId={boardGroupFieldId}
                recordId={recordId}
                present={preview.present(recordId)}
                onExited={() => preview.exited(recordId)}
                archived={archived}
                {...(draggable
                  ? { leading: <RowDragHandle recordId={recordId} groupKey={encoded} /> }
                  : {})}
              />
            ))}
            {group.records.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="data-cell border-b border-line-subtle">
                  <p className="text-sm text-ink-subtle">No records in this group.</p>
                </td>
              </tr>
            )}
            {group.count > group.records.length && (
              <tr>
                <td colSpan={columns.length} className="data-cell border-b border-line-subtle">
                  {/*
                    D19: THESE ROWS ARE A PREVIEW, NOT A FIRST PAGE. The grouped read's row
                    statement ranks with `row_number()` and therefore emits no cursor, so
                    asking for the rest starts at page 1 whatever we do - and the only real
                    question was whether the preview stays above it and appears twice. It
                    does not: the seeded rows are dropped and the list repaints.
                  */}
                  <Button variant="ghost" size="sm" onClick={() => display.expand(encoded)}>
                    Show all
                  </Button>
                  <span className="ml-2 text-xs text-ink-subtle">
                    {`Showing the first ${String(group.records.length)}.`}
                  </span>
                </td>
              </tr>
            )}
          </>
        ))}

      {open && grouped.plan.footerIndex.size > 0 && (
        <GroupTotalsRow columns={columns} grouped={grouped} totals={group} label="Group total" />
      )}
    </tbody>
  );
}

/**
 * A GROUP THAT ASKED FOR EVERYTHING: the ordinary infinite list, from page 1 (D19).
 *
 * Its filter is the view's AND the group's own condition, which is the same shape the board
 * has used per column since `record-page-and-views.md` D21 - and it is the whole reason
 * D11's second clause is a hard rule: a dimension whose group cannot be written as a
 * condition on the SAME field may not be a dimension, because otherwise this would need a
 * second pagination grammar.
 */
function ExpandedGroupRows({
  object,
  columns,
  fields,
  boardGroupFieldId,
  grouped,
  group,
  viewFilter,
  orderBy,
  select,
  archived,
  draggable,
}: Omit<GroupSectionProps, 'index' | 'display'>) {
  const filter = useMemo(
    () => groupRowsFilter(group.key, grouped.resolved, viewFilter),
    [group.key, grouped.resolved, viewFilter],
  );

  const list = useInfiniteRecordList(object.id, {
    select,
    filter,
    ...(orderBy === undefined ? {} : { orderBy }),
    // ONE PAGE OF A GROUP that has left its preview behind, at the server's own default
    // page size - `table-completion.md` D2: there is one page-size constant and this is it.
    limit: RECORD_QUERY_DEFAULT_LIMIT,
    archivedOnly: archived,
  });

  const ids = pagedIds(list.data) ?? [];
  const rows = usePresentRows(ids);

  if (list.isPending) {
    return (
      <>
        {PLACEHOLDER_GROUPS.slice(0, 3).map((key) => (
          <PlaceholderRow key={key} columns={columns} />
        ))}
      </>
    );
  }

  if (list.isError) {
    return (
      <tr>
        <td colSpan={columns.length} className="data-cell border-b border-line-subtle">
          <p role="alert" className="text-sm text-danger">
            {`These records could not be loaded. ${list.error.message}`}
          </p>
        </td>
      </tr>
    );
  }

  return (
    <>
      {rows.ids.map((recordId) => (
        <RecordTableRow
          key={recordId}
          object={object}
          columns={columns}
          fields={fields}
          boardGroupFieldId={boardGroupFieldId}
          recordId={recordId}
          present={rows.present(recordId)}
          onExited={() => rows.exited(recordId)}
          archived={archived}
          {...(draggable
            ? {
                leading: <RowDragHandle recordId={recordId} groupKey={encodeGroupKey(group.key)} />,
              }
            : {})}
        />
      ))}
      {list.hasNextPage === true && (
        <tr>
          <td colSpan={columns.length} className="data-cell border-b border-line-subtle">
            <Button
              variant="ghost"
              size="sm"
              loading={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              Show more
            </Button>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * THE TOTALS ROW, in the two places D18 puts it: once per group section, and once at the
 * bottom for the whole set. That is the matrix Airtable documents, and it costs nothing
 * extra - the grouped statement already carries every requested aggregate for every group,
 * and the grand total is the same statement with an empty `groupBy`.
 */
function GroupTotalsRow({
  columns,
  grouped,
  totals,
  label,
  grand = false,
}: {
  readonly columns: readonly TableColumn[];
  readonly grouped: GroupedTable;
  readonly totals: RecordGroupTotals | undefined;
  readonly label: string;
  /** The bottom one. A different KIND of number, so it reads as one. */
  readonly grand?: boolean;
}) {
  const first = columns[0];
  const labelInFirst = first !== undefined && !grouped.plan.footerIndex.has(first.field.id);

  return (
    /**
     * A SECTION TOTAL IS A QUIET LAST LINE, not a second grey band.
     *
     * The heading of a group is already `bg-surface-sunken`, and giving its total the same
     * fill put two grey bands next to each other with the next group's heading - one block
     * where there are two things. The section total keeps the row surface and a hairline;
     * the GRAND total keeps the fill and a heavier rule, because it is the one number that
     * is not about a section.
     */
    <tr className={grand ? 'bg-surface-sunken' : undefined}>
      {columns.map((column, index) => {
        const at = grouped.plan.footerIndex.get(column.field.id);
        const lines =
          at === undefined || totals === undefined
            ? []
            : aggregateLines(
                totals.aggregates[at],
                grouped.plan.requests[at]?.op ?? 'count',
                column.field,
              );
        return (
          <td
            key={column.field.id}
            className={cx(
              'data-cell align-top text-xs tabular-nums',
              grand
                ? 'border-t border-line text-ink'
                : 'border-t border-line-subtle text-ink-muted',
              column.align === 'right' ? 'text-right' : 'text-left',
            )}
          >
            {index === 0 && labelInFirst ? (
              <span className="text-2xs font-semibold tracking-widest whitespace-nowrap text-ink-muted uppercase">
                {label}
              </span>
            ) : (
              // While the read is in flight there is NO answer yet, and an em dash is an
              // answer: it means "no rows, or no non-null values" (D12). An empty cell is
              // what "not yet" looks like, and the container already carries `aria-busy`.
              lines.map((line) => (
                <span key={line} className="block whitespace-nowrap">
                  {line}
                </span>
              ))
            )}
          </td>
        );
      })}
    </tr>
  );
}

/**
 * THE PRODUCT'S FIRST `<tfoot>` (D22). Sticky at the bottom of the SCROLLPORT the way the
 * column head is sticky at its top - the card since the wave-2 reversal, the page before it,
 * and the rule is the same either way: the grand total is the one number a reader must not
 * have to hunt for. Deliberately WITHOUT a height of its own: a currency cell is one line
 * per code, so the row grows with the data and a fixed height would clip it.
 */
function GroupTotalsFoot({
  columns,
  grouped,
  totals,
  label,
  sticky,
}: {
  readonly columns: readonly TableColumn[];
  readonly grouped: GroupedTable;
  readonly totals: RecordGroupTotals | undefined;
  readonly label: string;
  readonly sticky: boolean;
}) {
  return (
    <tfoot
      className={cx(
        'layer-nav bg-surface-sunken',
        sticky && 'sticky bottom-0',
        // The one place a border does real work: the grand total is a different KIND of
        // number from the section totals above it, and the reader has to be able to tell.
        'border-t-2 border-line-strong',
      )}
    >
      <GroupTotalsRow columns={columns} grouped={grouped} totals={totals} label={label} grand />
    </tfoot>
  );
}

/** What a handle puts on the drag, read back without trusting dnd-kit's `any`. */
function sourceKeyOf(data: unknown): string | null {
  if (typeof data !== 'object' || data === null || !('groupKey' in data)) return null;
  const value = (data as { readonly groupKey: unknown }).groupKey;
  return typeof value === 'string' ? value : null;
}

/**
 * THE HANDLE, and it is a real `<button>` rather than the whole row.
 *
 * The board makes the entire card the drag activator, which is right there: a card is a
 * summary and nothing inside it is editable. A table CELL is its own editor, so a row-wide
 * activator would swallow the pointer that was on its way into a cell - and a mis-drag that
 * writes the group field is exactly the kind of accident this table must not have.
 */
function RowDragHandle({
  recordId,
  groupKey,
}: {
  readonly recordId: string;
  readonly groupKey: string;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: rowDragId(recordId),
    // The group the row is IN, carried with the drag rather than looked up afterwards.
    data: { groupKey },
    attributes: { roleDescription: 'Record row' },
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...(listeners ?? {})}
      aria-label="Move this record to another group"
      className={cx(
        'hit-expand shrink-0 cursor-grab touch-manipulation rounded-control p-0.5',
        'text-ink-subtle hover:text-ink focus-visible:outline-2 focus-visible:outline-accent',
        isDragging && 'opacity-40',
      )}
    >
      <Icon name="grip" size="0.9rem" />
    </button>
  );
}

/** What the pointer carries: the record's own title, never a uuid. */
function DraggedRowChip({
  objectId,
  recordId,
  titleFieldId,
}: {
  readonly objectId: string;
  readonly recordId: string;
  readonly titleFieldId: string;
}) {
  const queryClient = useQueryClient();
  // A synchronous cache read is exactly what an overlay needs and a hook is not; a missing
  // entry degrades to a placeholder rather than to a uuid.
  const row = queryClient.getQueryData<RecordRow>(queryKeys.record(objectId, recordId));
  return (
    /* `w-max`, because dnd-kit sizes the overlay wrapper to the DRAGGED NODE - which here
       is an 18px handle, not a row. Without it the chip is a 36px square with the title
       squeezed out of it. */
    <div className="flex w-max items-center gap-2 rounded-control border border-line-strong bg-surface px-3 py-2 text-sm whitespace-nowrap shadow-popover">
      <Icon name="grip" size="0.9rem" className="text-ink-subtle" />
      {row === undefined ? (
        <Skeleton className="h-3 w-24" />
      ) : (
        <span className="truncate">{recordTitle(row, titleFieldId)}</span>
      )}
    </div>
  );
}

/** Exported for the in-flight indicator the view header renders (D24). */
export function GroupFetchingIndicator({ active }: { readonly active: boolean }) {
  if (!active) return null;
  return (
    <span className="flex items-center gap-2 text-xs text-ink-muted">
      <Spinner size="0.8rem" />
      Updating the totals
    </span>
  );
}
